import React, { useEffect, useState } from 'react';
import { EMAIL_TEMPLATE_VARIABLES } from '@claimup/shared/constants/email-templates';
import { getPreviewValues } from '../../services/templatePreviewService';
import { partirPorVariables } from './partirPorVariables';

/**
 * «Así lo verá el deudor»: la plantilla que se está escribiendo, ya rellenada.
 *
 * Quien escribe un mensaje no veía nunca el texto final — el editor enseñaba
 * `{{debt_title}}` con las llaves, y el valor solo aparecía al mandarlo. Así
 * estuvo meses saliendo el CIF del deudor donde ponía «factura».
 *
 * Las dos cosas que tiene que hacer a la vez, y que tiran en direcciones
 * opuestas: **leerse como lo leerá el deudor** (si no, no sirve para juzgar la
 * frase) y **dejar ver qué trozo es variable** (si no, se confunde con texto
 * escrito). De ahí el chip: el valor a la vista, con fondo, y el nombre de la
 * variable al pasar por encima.
 *
 * Ver docs/previa-editor-plantillas-plan.md.
 */

/** Lo que falta se enseña con el mismo convenio que usa el render del backend. */
const hueco = nombre => `⟨${nombre}⟩`;

const Tramo = ({ tramo, valores }) => {
  if (tramo.tipo === 'texto') return <>{tramo.valor}</>;

  const valor = valores?.[tramo.nombre];
  const que = EMAIL_TEMPLATE_VARIABLES[tramo.nombre];
  const descripcion = que ? `${tramo.nombre} — ${que}` : tramo.nombre;

  if (valor === undefined || valor === null || String(valor).trim() === '') {
    return (
      <span
        title={`${descripcion}. Este expediente no lo tiene.`}
        aria-label={`${tramo.nombre}, sin dato`}
        className='rounded-lg bg-red-50 px-1 text-red-700 ring-1 ring-red-200'
      >
        {hueco(tramo.nombre)}
      </span>
    );
  }

  return (
    <span
      title={descripcion}
      aria-label={`${tramo.nombre}: ${valor}`}
      className='rounded-lg bg-amber-50 px-1 text-slate-900 ring-1 ring-amber-200'
    >
      {valor}
    </span>
  );
};

/** Un texto de la plantilla, resuelto. Respeta los saltos de línea del original. */
export const TextoResuelto = ({ texto, valores, className = '' }) => (
  <p className={`whitespace-pre-wrap break-words ${className}`}>
    {partirPorVariables(texto).map((tramo, i) => (
      <Tramo key={i} tramo={tramo} valores={valores} />
    ))}
  </p>
);

/**
 * @param {{asunto?: string, cuerpo: string, companyId?: string}} props
 */
const PreviaDePlantilla = ({ asunto, cuerpo, companyId }) => {
  const [datos, setDatos] = useState(null);
  const [conReal, setConReal] = useState(true);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    getPreviewValues(companyId)
      .then(d => {
        if (!vivo) return;
        setDatos(d);
        // Una empresa recién dada de alta no tiene expedientes: la previa no se
        // queda en blanco, se queda en ejemplos.
        setConReal(Boolean(d?.real));
      })
      .catch(() => vivo && setDatos(null))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, [companyId]);

  const hayReal = Boolean(datos?.real);
  const valores = conReal && hayReal ? datos.real.values : datos?.examples;

  if (!cuerpo?.trim() && !asunto?.trim()) return null;

  return (
    <section
      aria-label='Así lo verá el deudor'
      className='rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2'
    >
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <p className='text-xs font-medium text-slate-700'>Así lo verá el deudor</p>

        {/* El interruptor solo tiene sentido si hay con qué comparar. */}
        {hayReal && (
          <div className='flex items-center gap-1 text-xs'>
            {[
              { id: false, texto: 'Datos de ejemplo' },
              { id: true, texto: 'Un expediente real' },
            ].map(opcion => (
              <button
                key={String(opcion.id)}
                type='button'
                onClick={() => setConReal(opcion.id)}
                aria-pressed={conReal === opcion.id}
                className={`rounded-lg px-2 py-1 ${
                  conReal === opcion.id
                    ? 'bg-white text-slate-900 ring-1 ring-slate-300'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {opcion.texto}
              </button>
            ))}
          </div>
        )}
      </div>

      {cargando && <p className='text-xs text-slate-400'>Cargando los datos…</p>}

      {!cargando && (
        <>
          {asunto?.trim() ? (
            <TextoResuelto
              texto={asunto}
              valores={valores}
              className='text-sm font-medium text-slate-900'
            />
          ) : null}

          {cuerpo?.trim() ? (
            <TextoResuelto texto={cuerpo} valores={valores} className='text-sm text-slate-700' />
          ) : null}

          <p className='text-xs text-slate-500'>
            {conReal && hayReal
              ? `Con el expediente de ${datos.real.debtor_name || 'un deudor real'}.`
              : hayReal
                ? 'Con datos de ejemplo: los que se le mandan a Meta para aprobar la plantilla.'
                : 'Con datos de ejemplo. Esta empresa todavía no tiene expedientes de AR.'}
          </p>
        </>
      )}
    </section>
  );
};

export default PreviaDePlantilla;
